import { can } from '@misumi/shared'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { PlusOutlined } from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { Tabs } from '../../components/ui/Tabs'
import { useRole } from '../../lib/auth/use-session'
import type { CursorListState } from '../../lib/list/useCursorList'
import { useLoadMoreSentinel } from '../../lib/list/useLoadMoreSentinel'
import { countOpenDrafts, isOpenDraftSlot, useRfqBundle } from '../../lib/rfq/rfq-bundle-context'
import type { RfqDraftSlot } from '../../lib/rfq/rfq-bundle-context'
import { useRfqCount } from '../../lib/rfq/rfq-count-context'
import { NewRfqModal } from './NewRfqModal'
import { RfqDraftPanel } from './RfqDraftPanel'
import { RfqListRow } from './RfqListRow'
import { nonDraftRfqCount, summarizeDraft, visibleRfqTabs } from './domain'
import type { RfqListResponse, RfqServerTabId, RfqTabId } from './domain'
import { useRfqList } from './useRfqList'

/** ADR-0056 (UID-135) Decision 1 — the shape `RfqListRow`'s "View RFQ →"
 * navigation writes onto the CURRENT `/rfq` history entry (a `replace`
 * navigate) right before pushing to the detail route, so a later
 * `navigate(-1)` from the detail page's "← Back" pops back here with this
 * state intact. */
interface RfqListLocationState {
  readonly fromTab?: RfqTabId
  readonly fromScrollY?: number
  readonly fromRfqId?: string
}

type LoadState = 'loading' | 'loaded' | 'error'

/** UID-603 (ADR-0176 D6 row 2) — the per-tab live-region wording, reading
 * `totalCount` (the count matching that tab's server-side `status` filter),
 * NEVER `items.length` — otherwise the announcement would report the PAGE
 * size instead of how many RFQs actually matched. */
function loadedAnnouncement(totalCount: number | undefined): string {
  if (totalCount === undefined) return 'RFQs loaded.'
  return totalCount === 0 ? 'RFQs loaded. No RFQs yet.' : `RFQs loaded — ${totalCount}.`
}

/** UID-294 (§9) — per-tab dashed empty-state copy (`Fictiv Platform.dc.html`
 * ~L3442's dashed-box pattern; title + a matching one-line subtitle, no
 * icon). The Draft tab's own "No draft in progress" dashed box from the
 * same design region is NOT included here: `RfqDraftPanel` renders it
 * itself (its `partCount === 0` branch, ADR-0101/ADR-0172 D5) rather than
 * this per-server-tab map, which only ever covers the three server tabs. */
const EMPTY_TAB_MESSAGE: Record<
  RfqServerTabId,
  { readonly title: string; readonly detail: string }
> = {
  requested: {
    title: 'No requested RFQs',
    detail: "RFQs you submit will show up here while they're being sourced.",
  },
  quoted: {
    title: 'No quoted RFQs',
    detail: "Once a quote comes back on a request, you'll see it here.",
  },
  expired: {
    title: 'No expired RFQs',
    detail: 'RFQs that passed their quote validity window will show up here.',
  },
}

/**
 * Quotes/RFQs screen (screens.md §14, `/rfq`, gated `rfq:submit`).
 *
 * UID-603 (ADR-0176 D1.3/D5/D6 row 2) — the account's RFQs are no longer
 * fetched once as a bare newest-first array and bucketed client-side through
 * `groupByTab`. `GET /rfq` now returns the `{ items, nextCursor, totalCount,
 * statusCounts }` cursor-page envelope and takes a repeatable server-side
 * `?status=` filter, so each of the three server tabs is its own
 * independently cursor-paginated read (`useRfqList`, on the shared
 * `useCursorList` hook), and every whole-population number on this screen —
 * the tab badges, the tab-strip visibility gate, the default-tab pick, the
 * live-region announcement — reads the page-independent `statusCounts` /
 * `totalCount` rather than a loaded page's `.length`.
 */
export function RfqListPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as RfqListLocationState | null
  const role = useRole()
  const canSubmitRfq = role !== null && can(role, 'rfq:submit')
  // ADR-0056 (UID-135) Decision 1: initialize from the router state a
  // returning `RfqListRow` "View RFQ →" navigation left on THIS entry
  // (see the `replace` navigate in RfqListRow.tsx), defaulting to
  // 'requested' for a fresh/direct load — never a `?tab=` URL param.
  //
  // Declared BEFORE `useRfqList` because that hook is now driven by it: tabs
  // are fetched lazily per visit, so the active tab is an INPUT to the fetch,
  // not just a render-time selector over three already-fetched tabs.
  const [activeTab, setActiveTab] = useState<RfqTabId>(locationState?.fromTab ?? 'requested')
  // UID-603 (ADR-0176) — the three server tabs are fetched independently,
  // each cursor-paginated + server-status-filtered, and LAZILY: only
  // `activeTab` loads on mount, each other tab on its first visit, with no
  // refetch on a revisit (see `useRfqList`'s doc comment, including why the
  // tab badges do not depend on having fetched all three).
  const { byTab, statusCounts, tabBadgeCounts, initialLoadPending, allTabsErrored, reloadAll } =
    useRfqList(activeTab)
  // UID-289 (ADR-0094 D6 amendment) — the "+ New RFQ" button opens this
  // lightweight pick-and-hand-off modal instead of navigating straight to
  // `/rfq/new`. Conditionally MOUNTED (not just visually hidden) so the
  // modal's own `GET /parts?status=quote` fetch only fires once actually
  // opened, matching this page's `AddToProjectModal`-style sibling-modal
  // convention elsewhere in this codebase.
  const [newRfqModalOpen, setNewRfqModalOpen] = useState(false)
  // UID-113 (ADR-0049): the Draft tab is a client-bundle-driven surface, not
  // a server RFQ status bucket — see `visibleRfqTabs`/`summarizeDraft` in
  // `domain.ts`. ADR-0101 (UID-309) supersedes ADR-0049's show/hide-on-count
  // visibility rule specifically: the tab is now always rendered (badge "0"
  // when there are no open drafts) — everything else about it being
  // bundle-driven, not server-driven, still holds.
  //
  // UID-551 (ADR-0172 D2) — the tab now renders ONE CARD PER SLOT
  // (`rfqBundle.slots`), newest-first by `slot.createdAt` (D3), instead of a
  // single card fed from the flat active-slot projections. `hasDraft` is
  // retired from this surface entirely (ADR-0172 D4 "kill the boolean") —
  // `draftPresent` below is retargeted to `countOpenDrafts(slots) > 0`, the
  // SAME filtered count the badge and the card list use. Not a bare
  // `slots.length > 0`: that was the C1 bug (a zero-item slot renders no
  // card), so naming it here would invite reintroducing it.
  const rfqBundle = useRfqBundle()
  // UID-603 (ADR-0176 D5) — `statusCounts` is the page-independent,
  // UNFILTERED non-draft total (`nonDraftRfqCount`), the SAME derivation
  // `useHydrateRfqCount` (the sidebar badge, `AppShell`) computes from its
  // own `/rfq` call. Pushing it here lets that hook skip its own redundant
  // fetch on this exact route (see its doc comment) without changing what
  // the badge ends up showing — unchanged perf posture, now sourced from
  // `statusCounts` instead of `data.filter(...).length`.
  const { setTotalCount } = useRfqCount()
  useEffect(() => {
    if (statusCounts) setTotalCount(nonDraftRfqCount(statusCounts))
  }, [statusCounts, setTotalCount])
  const [draftAnnouncement, setDraftAnnouncement] = useState('')
  const headingRef = useRef<HTMLHeadingElement | null>(null)
  // ADR-0056 Decision 1: restore scroll position exactly ONCE, after the
  // list's async load resolves (the list is zero-height at first paint, so
  // native scroll restoration alone would land at the top) — never on a
  // later reload triggered by `onChanged`.
  const scrollRestoredRef = useRef(false)
  // UID-289 — the default-tab pick (below) runs exactly ONCE, after the
  // initial load resolves — never on a later reload (`onChanged`), which
  // would otherwise yank the customer off a tab they've since navigated to.
  const initialTabPickedRef = useRef(false)

  // UID-603 — the page's overall load state derives from `useRfqList`'s
  // anchor (first-loaded) tab: 'loading' until that tab's first page has
  // settled, 'error' only if every tab fetched so far failed — leaving the
  // screen with no `statusCounts` to render a tab strip from — and 'loaded'
  // otherwise. A single later-visited tab failing is that tab's own inline
  // error (see the tab body below), never a page-level one. It is
  // deliberately NOT re-entered when a lazily-visited tab starts loading:
  // that would blank the tab strip and the current rows on every tab switch.
  const loadState: LoadState = initialLoadPending ? 'loading' : allTabsErrored ? 'error' : 'loaded'
  const activeServerTab = activeTab === 'draft' ? null : (activeTab as RfqServerTabId)
  const activeTabState = activeServerTab ? byTab[activeServerTab].state : null
  const liveMessage =
    loadState === 'loading'
      ? 'Loading RFQs.'
      : loadState === 'error'
        ? 'RFQs failed to load.'
        : activeTabState?.status === 'ready'
          ? loadedAnnouncement(activeTabState.page.totalCount)
          : activeTabState?.status === 'error'
            ? 'RFQs failed to load.'
            : 'Loading RFQs.'

  // ADR-0056 Decision 1: restore the scroll offset once the list has its
  // real height (after `loadState` first becomes 'loaded') — router state
  // first, a `sessionStorage` backstop second (survives a hard reload
  // mid-flow, when router state may not), then clear the backstop.
  //
  // Review W5 (fifth pass) — UID-603 paginates the active tab at
  // `RFQ_LIST_PAGE_SIZE` instead of loading it whole, so "the list has its
  // real height" is no longer true after just page 1: a saved offset past
  // that page's bottom used to `window.scrollTo` land on the tab's actual
  // content (pre-UID-603, the whole list was already mounted) and now
  // clamps short of it. `scrollRestoreTargetRef` holds the pending target
  // (or `null` once resolved/not applicable) across renders; the second
  // effect below keeps calling `loadMore()` on the active tab until either
  // the target is reachable or the tab runs out of pages.
  const scrollRestoreTargetRef = useRef<number | null>(null)
  const scrollRestoreAttemptsRef = useRef(0)
  useEffect(() => {
    if (loadState !== 'loaded' || scrollRestoredRef.current) return
    scrollRestoredRef.current = true
    const backstopKey = `rfq:list:scroll:${activeTab}`
    const backstop = sessionStorage.getItem(backstopKey)
    const scrollY = locationState?.fromScrollY ?? (backstop ? Number(backstop) : undefined)
    scrollRestoreTargetRef.current = scrollY ?? null
  }, [loadState, activeTab, locationState])

  const finishScrollRestore = useCallback(
    (scrollY: number) => {
      window.scrollTo(0, scrollY)
      const backstopKey = `rfq:list:scroll:${activeTab}`
      try {
        sessionStorage.removeItem(backstopKey)
      } catch {
        // sessionStorage unavailable — nothing left to clean up.
      }
      // §1.3 focus-on-back: return focus to the row's "View RFQ →" link the
      // user came from, if it's still in the DOM after the tab restored;
      // otherwise fall back to the page heading rather than stranding focus
      // on <body>.
      const originLink = locationState?.fromRfqId
        ? document.getElementById(`rfq-view-link-${locationState.fromRfqId}`)
        : null
      ;(originLink ?? headingRef.current)?.focus()
      scrollRestoreTargetRef.current = null
    },
    [activeTab, locationState],
  )

  // A max bound on how many extra pages this will fetch chasing a saved
  // offset — a stale target (rows since deleted, or a target from a much
  // longer list) must not turn into an unbounded fetch loop.
  const MAX_SCROLL_RESTORE_LOAD_MORE_ATTEMPTS = 20
  useEffect(() => {
    const target = scrollRestoreTargetRef.current
    if (target === null) return
    // The draft tab (client-side, never paginated) and any server tab with
    // no further pages already have all their content mounted.
    const tab = activeServerTab ? byTab[activeServerTab] : null
    const tabState = tab?.state
    const hasMorePages = tabState?.status === 'ready' && tabState.page.nextCursor != null
    if (!hasMorePages) {
      finishScrollRestore(target)
      return
    }
    if (document.documentElement.scrollHeight >= target + window.innerHeight) {
      finishScrollRestore(target)
      return
    }
    if (tabState.isLoadingMore) return // already fetching — re-checked on the next render
    if (scrollRestoreAttemptsRef.current >= MAX_SCROLL_RESTORE_LOAD_MORE_ATTEMPTS) {
      finishScrollRestore(target) // best effort — as far as loaded content allows
      return
    }
    scrollRestoreAttemptsRef.current += 1
    tab?.loadMore()
  }, [byTab, activeServerTab, finishScrollRestore])

  // UID-603 — `hasAnyRfqAtAll` now reads the page-independent `statusCounts`
  // (D5: "Tab-strip visibility … read `statusCounts`, not the page"), not a
  // loaded page's `.length`. `null` (nothing has resolved yet) reads as "no
  // RFQs" rather than flashing the tab strip before the count is known.
  const hasAnyRfqAtAll = statusCounts !== null && nonDraftRfqCount(statusCounts) > 0
  // UID-551 (ADR-0172 D5, as corrected by code review C1) — the ONE list of
  // slots this tab renders a card for, filtered through the SHARED
  // `isOpenDraftSlot` predicate that `countOpenDrafts` (the badge) also
  // applies. Deriving both the badge and the card list from the same filter
  // is what makes them agree BY CONSTRUCTION rather than by coincidence: an
  // items-less slot renders no card (`RfqDraftPanel`'s `partCount === 0`
  // branch is the tab-wide zero-state, not a card), so counting one produced
  // a "Draft 1" badge above a zero-state — or a dashed empty-state card
  // wedged between real ones. Sorted newest-first by the slot's OWN
  // `createdAt` (D3 — never by `rfqId`/`rfqNumber`, which a slot may not have
  // and whose server rank can legitimately disagree with client mint order);
  // `.sort()` runs on the copy `filter` already produced, never on `slots`.
  const draftSlots = rfqBundle.slots
    .filter(isOpenDraftSlot)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  // ADR-0172 D4 — retargeted from `hasDraft(rfqBundle.items)` to the slot
  // count: a boolean presence check over the flat active-slot's items would
  // now silently miss every OTHER open slot when the active one happens to
  // be empty. Code review C1 — and it reads the SAME filtered count the
  // badge and the card list do, so the tab strip cannot appear "because a
  // draft exists" while the tab itself shows its zero-state.
  const draftPresent = countOpenDrafts(rfqBundle.slots) > 0
  // A draft-in-progress is worth surfacing even if the account has never
  // submitted a real RFQ yet — the true-empty state is about SUBMITTED
  // RFQs, not the in-progress client bundle, so it does not swallow the
  // Draft tab (Gate 2 spec: the Draft tab's only visibility condition is
  // "a draft exists").
  // ADR-0101 (UID-309, corrected 2026-08-26) — the reference `.dc.html`
  // renders its 4-tab `rfqTabs` strip and the Draft tab unconditionally: it
  // has no role/capability model at all, so there is no "Purchaser can't
  // see Draft" case to defend against there. A prior Gate 5 remediation had
  // filtered the Draft tab (and gated `showTabStrip`'s draft-only branch) on
  // `canSubmitRfq`, reasoning that a role without `rfq:submit` would hit a
  // stranded link on "Resume draft" — but that traded away literal fidelity
  // to the design source of truth (rule 8: `.dc.html` always wins) for a
  // narrowing the reference itself doesn't make. Reverted: the tab strip and
  // the Draft tab are both unconditional again, for every role. The actual
  // authorization boundary is unaffected — `RfqReviewPage`'s server-enforced
  // `can(role, 'rfq:submit')` check still 403s the build/submit screen itself
  // for a role without the capability; only this list screen's DISPLAY gate
  // is removed.
  const showTabStrip = hasAnyRfqAtAll || draftPresent
  const visibleTabsForRole = visibleRfqTabs(draftPresent)

  // UID-289 (Bucket A item 4) — default the list to the first NON-EMPTY tab
  // (in `visibleRfqTabs`'s array order) once the initial load resolves,
  // rather than always hardcoding 'requested' — a customer whose account
  // has no `requested` RFQs no longer lands on a tab that's empty by
  // construction. An explicit `fromTab` router-state restoration (ADR-0056
  // Decision 1, back-navigation from the detail page) always wins and is
  // never overridden — this only picks a DEFAULT for a fresh/direct load.
  useEffect(() => {
    if (loadState !== 'loaded' || initialTabPickedRef.current) return
    initialTabPickedRef.current = true
    if (locationState?.fromTab) return
    // UID-603 (ADR-0176 D5) — reads `tabBadgeCounts` (folded from
    // `statusCounts`), not a loaded page's `.length`.
    const firstNonEmpty = visibleTabsForRole.find((tab) =>
      tab.id === 'draft' ? draftPresent : tabBadgeCounts[tab.id as RfqServerTabId] > 0,
    )
    if (firstNonEmpty) setActiveTab(firstNonEmpty.id)
  }, [loadState, draftPresent, tabBadgeCounts, locationState, visibleTabsForRole])

  // UID-551 (ADR-0172 D5) — takes the SPECIFIC slot's id and discards ONLY
  // that slot (`discardSlot`, not the flat active-slot `discard()`). This is
  // the highest-risk change in this phase: every card must call this with
  // ITS OWN `slotId`, never a page-level "the" draft, or confirming on one
  // card would discard a sibling's.
  function handleDraftDiscarded(slotId: string) {
    rfqBundle.discardSlot(slotId)
    // Gate 5 code review (LOW-2) — ADR-0101 made the Draft tab
    // UNCONDITIONAL (it no longer disappears once every slot empties out;
    // it now shows the panel's zero-state instead, see `RfqDraftPanel`'s
    // `partCount === 0` branch). The prior comment/behavior here predated
    // that change and was stale: it force-switched `activeTab` to
    // 'requested' on the theory that the Draft tab was "about to
    // disappear" — which is no longer true, and forcing the switch now just
    // yanks the customer away from the tab they were looking at, onto a
    // different one, right after they discarded. `activeTab` is left alone;
    // the Draft tab simply re-renders (fewer cards, or its zero-state) in
    // place.
    setDraftAnnouncement('Draft discarded.')
    // Accessibility: the Discard button (and the whole populated Draft
    // panel) is about to be replaced by the zero-state content; move focus
    // to a stable landmark — the page heading — rather than letting focus
    // silently fall back to <body>. Called synchronously (not from an
    // effect) so it takes effect before the confirm dialog's own
    // focus-restore-on-close runs against what is about to become a
    // detached node.
    headingRef.current?.focus()
  }

  return (
    <section aria-labelledby="rfq-list-heading">
      <div className="rfq-list-header">
        <div className="rfq-list-heading-col">
          <h1 id="rfq-list-heading" className="display-heading" ref={headingRef} tabIndex={-1}>
            My RFQs
          </h1>
          <p className="rfq-list-subtitle">
            Quotes for custom parts — review returned pricing and check out directly.
          </p>
        </div>
        {/* UID-56 entry point: only a role holding `rfq:submit` (Engineer)
         * sees this — a Purchaser never gets a link into a screen it can't
         * use (see registry.tsx's doc comment on this vertical). */}
        {canSubmitRfq && (
          <Button
            variant="secondary"
            className="rfq-new-request-btn"
            onClick={() => setNewRfqModalOpen(true)}
          >
            <PlusOutlined aria-hidden="true" />
            New RFQ
          </Button>
        )}
      </div>
      <p aria-live="polite" className="sr-only">
        {liveMessage}
      </p>
      {/* UID-113: a separate live region for the Draft tab's own state
       * changes (discard), so it doesn't collide with / get raced by the
       * list-load announcer above. */}
      <p aria-live="polite" className="sr-only">
        {draftAnnouncement}
      </p>

      {loadState === 'loading' && <p>Loading your RFQs…</p>}

      {loadState === 'error' && (
        <div role="alert" className="table-error-panel">
          <p>Couldn&rsquo;t load your RFQs.</p>
          <button type="button" className="btn-link" onClick={reloadAll}>
            Try again
          </button>
        </div>
      )}

      {loadState === 'loaded' && !showTabStrip && (
        <EmptyState title="You haven't requested any quotes yet" />
      )}

      {loadState === 'loaded' && showTabStrip && (
        <Tabs
          // UID-289 (ADR-0065 amendment) — ratified boxed segmented-control
          // skin, scoped to this screen only (ui.css `.tabs-segmented`).
          className="tabs-segmented"
          // UID-204: Draft renders FIRST, matching `visibleRfqTabs`'s
          // `RFQ_TABS` array order (ADR-0049). The Gate 2 design's
          // `rfqTabs` array has no Draft tab at all, so it does not dictate
          // a draft-last position — there is no display reordering here.
          items={visibleTabsForRole.map((tab) => ({
            ...tab,
            // UID-115 (main) changed TabItem's badge API from a plain `count`
            // to the discriminated `badge: TabBadge` union (Tabs.tsx) — adapt
            // the Draft tab's bundle-derived count to that shape rather than
            // the server-tab-only `groups[tab.id].length` main's version used.
            //
            // UID-485 c1 — the Draft badge counts CARDS, not parts. It used to
            // be `rfqBundle.items.length`, i.e. the number of queued PARTS on
            // the single active slot — so 3 queued parts read "Draft 3" above
            // a single card. UID-551 (ADR-0172 D4) makes the Draft badge use
            // `countOpenDrafts(rfqBundle.slots)` — the ONE shared seam this
            // file and `Sidebar.tsx`'s nav badge both read, so the two can
            // never independently drift (do not inline `rfqBundle.slots.length`
            // here instead). The bundle's part count is still surfaced
            // per-card, in each card's own meta line (`RfqDraftPanel`).
            //
            // UID-603 (ADR-0176 D5) — every OTHER tab's badge now reads
            // `tabBadgeCounts` (folded from the page-independent
            // `statusCounts`), NOT `groups[tab.id].length` — a loaded page's
            // row count would report only what's been scrolled into view so
            // far, not the true per-status total.
            badge: {
              kind: 'count' as const,
              count:
                tab.id === 'draft'
                  ? countOpenDrafts(rfqBundle.slots)
                  : tabBadgeCounts[tab.id as RfqServerTabId],
            },
          }))}
          activeId={activeTab}
          onChange={(id) => setActiveTab(id as RfqTabId)}
          aria-label="Filter RFQs by status"
        >
          {(activeId) => {
            const tabId = activeId as RfqTabId
            if (tabId === 'draft') {
              // UID-551 (ADR-0172 D2/D3/D5) — one card per RENDERABLE slot,
              // newest-first. Both the collection and its ordering are
              // computed once, above, as `draftSlots` — see that binding's
              // comment for why the `isOpenDraftSlot` filter is shared with
              // the badge rather than re-spelled here.
              const sortedSlots = draftSlots
              if (sortedSlots.length === 0) {
                // ADR-0101 / ADR-0172 D5 — zero slots keeps the existing
                // zero-state panel (not an absent tab, not an empty list):
                // feeding `RfqDraftPanel` an empty summary hits its own
                // `partCount === 0` branch. No slot exists to resume/discard,
                // so Resume simply navigates without activating anything
                // (there is nothing to activate) and Discard is unreachable
                // (the zero-state renders no Discard button).
                return (
                  <RfqDraftPanel
                    summary={summarizeDraft([], '')}
                    draftRfqId={undefined}
                    draftRfqNumber={undefined}
                    creatingDraft={false}
                    onResume={() => navigate('/rfq/new')}
                    onDiscardConfirmed={() => {}}
                  />
                )
              }
              return (
                <ul className="rfq-card-list">
                  {sortedSlots.map((slot: RfqDraftSlot) => (
                    <li key={slot.slotId}>
                      <RfqDraftPanel
                        summary={summarizeDraft(slot.items, slot.draft.needByDate)}
                        draftRfqId={slot.rfqId}
                        draftRfqNumber={slot.rfqNumber}
                        creatingDraft={slot.creating}
                        // Code review W6 (a11y) — with N cards on screen,
                        // every card's Discard/"Resume draft" button would
                        // otherwise carry an IDENTICAL accessible name, so a
                        // screen-reader user tabbing the list hears "Discard,
                        // button" three times with nothing to tell them
                        // apart. A slot that has not yet minted an `rfqId`
                        // has no number to name it by, so it falls back to a
                        // STABLE per-slot identifier (`slot.slotId`, which is
                        // immutable for the slot's whole life) rather than to
                        // a positional index, which would silently relabel
                        // every card whenever one is discarded.
                        draftLabel={`#${slot.slotId.slice(0, 8)}`}
                        onResume={() => {
                          rfqBundle.activateDraft(slot.slotId)
                          navigate('/rfq/new')
                        }}
                        onDiscardConfirmed={() => handleDraftDiscarded(slot.slotId)}
                      />
                    </li>
                  ))}
                </ul>
              )
            }
            return <RfqServerTabPanel tabId={tabId} tabList={byTab[tabId]} />
          }}
        </Tabs>
      )}

      {newRfqModalOpen && <NewRfqModal open onClose={() => setNewRfqModalOpen(false)} />}
    </section>
  )
}

/**
 * UID-603 (ADR-0176 D1.4/D6 row 2) — one server tab's (Requested/Quoted/
 * Expired) row list, infinite-scrolled via the SAME shared cursor hook
 * `usePartsLibrary` builds on. Split out of `RfqListPage`'s render body so
 * the `useLoadMoreSentinel` hook (which must run per-tab, since each tab is
 * independently paginated) isn't called conditionally inside a callback.
 */
function RfqServerTabPanel({
  tabId,
  tabList,
}: {
  readonly tabId: RfqServerTabId
  readonly tabList: {
    readonly state: CursorListState<RfqListResponse>
    readonly loadMore: () => void
    readonly reload: () => void
  }
}) {
  const { state, loadMore, reload } = tabList
  const hasNextPage = state.status === 'ready' && state.page.nextCursor !== null
  // Code-review C1 (third pass)/H4 (fourth pass) — `recheckKey` forces the
  // observer to re-check the sentinel's current intersection state every
  // time a page is appended, covering the case where the newly-appended
  // page is ALSO filtered away by this tab's own client-side filtering,
  // leaving the sentinel still intersecting without ever crossing a fresh
  // threshold, which would otherwise stall pagination one page in. Same
  // pattern as `NewRfqModal.tsx`'s `recheckKey` (see the hook's `recheckKey`
  // doc comment for the full mechanism).
  //
  // H4 (fourth pass) — a bare loaded-item COUNT is lossy here: if a page's
  // rows fully dedupe against what's already loaded (`useCursorList.ts`'s
  // `dedupeAppend`, the `updated_at`-churn case its own test models),
  // `mergedItems` comes back identical by reference AND length — the count
  // never changes even though a real fetch completed and `nextCursor`
  // advanced, so the observer never re-checks and pagination can still
  // stall with a next page genuinely available. `nextCursor` itself changes
  // on every successful `loadMore()` (advancing toward, or reaching, the
  // end) independent of whether the page's rows deduped away, so it's used
  // as the recheck key instead.
  const sentinelRecheckKey = state.status === 'ready' ? state.page.nextCursor : null
  const sentinelRef = useLoadMoreSentinel(loadMore, hasNextPage, {
    recheckKey: sentinelRecheckKey,
  })

  if (state.status === 'loading' || state.status === 'idle') {
    return <p>Loading your RFQs…</p>
  }
  if (state.status === 'error') {
    return (
      <div role="alert" className="table-error-panel">
        <p>Couldn&rsquo;t load your RFQs.</p>
        {/* Code-review W4 — a lazily-visited tab's own first-page failure
          used to have no retry affordance (only the page-level panel's
          `reloadAll` did, gated on every visited tab having failed). Retries
          just this tab, via `useCursorList.reload` threaded through
          `RfqTabListResult.reload`. */}
        <button type="button" className="btn-link" onClick={reload}>
          Try again
        </button>
      </div>
    )
  }
  const tabRfqs = state.page.items
  // Code-review H3 (fourth pass) — this used to return the TERMINAL empty
  // state whenever `tabRfqs.length === 0`, regardless of `hasNextPage`. A
  // page that comes back with zero rows but a non-null `nextCursor` (a
  // genuinely empty page that isn't the end of the list — currently latent
  // because `routes.ts` refuses a bare `workspaceId + limit` query, but not
  // guaranteed to stay that way) returned BEFORE the sentinel block below,
  // unmounting it along with everything else: no observer, no retry
  // affordance, the tab just permanently dead-ends. Same defect class C1/the
  // second pass already fixed for `WorkspacePage.tsx`/`PartsLibraryPage.tsx`
  // (keep the sentinel mounted off `hasNextPage`, not row count) — same fix
  // here: the terminal "no RFQs" verdict only renders once there's truly no
  // more to load.
  if (tabRfqs.length === 0 && !hasNextPage) {
    const emptyMessage = EMPTY_TAB_MESSAGE[tabId]
    return <EmptyState variant="dashed" title={emptyMessage.title} detail={emptyMessage.detail} />
  }
  return (
    <>
      {tabRfqs.length > 0 && (
        <ul className="rfq-card-list">
          {tabRfqs.map((rfq) => (
            <li key={rfq.id}>
              <RfqListRow rfq={rfq} activeTab={tabId} />
            </li>
          ))}
        </ul>
      )}
      {/* UID-603 — infinite-scroll sentinel, same pattern as
        `PartsLibraryPage`'s: ~600px lead so the next page is already
        loading before the shopper reaches the true bottom. Renders no
        markup/observer once the tab has no next page. Rendered
        unconditionally on `hasNextPage` (H3, fourth pass) — NOT on
        `tabRfqs.length`, so a zero-row-but-more-to-load page keeps this
        mounted rather than dead-ending the tab. */}
      {hasNextPage && (
        <div ref={sentinelRef} className="rfq-load-more-sentinel">
          <output aria-live="polite" className="rfq-load-more-status">
            {state.isLoadingMore ? 'Loading more RFQs…' : ''}
          </output>
          {state.loadMoreError && (
            <div role="alert" className="rfq-load-more-error">
              <p>{state.loadMoreError}</p>
              {/* M2 (fourth pass) — a cursor the server has rejected as
                unresolvable (`rfq-list-cursor.ts`'s deliberate 400) can
                never succeed via `loadMore()`, which just re-issues the
                SAME dead cursor forever. `isCursorRejected` distinguishes
                that case from a generic network/500 failure and retries via
                `reload()` (reset to page 1) instead. */}
              <button
                type="button"
                className="btn-link"
                onClick={state.loadMoreErrorIsCursorRejection ? reload : loadMore}
              >
                Try again
              </button>
            </div>
          )}
        </div>
      )}
    </>
  )
}
